import { Command } from 'cmdk';
import { useUnit } from 'effector-react';
import {
  BarChart3,
  Bell,
  ChartCandlestick,
  Eye,
  Keyboard,
  Languages,
  Layers,
  ListOrdered,
  LogOut,
  Monitor,
  Moon,
  Plus,
  Settings,
  ShoppingCart,
  Sun,
} from 'lucide-react';
import { Dialog as RadixDialog } from 'radix-ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { $positions, $selected, nextLevelOf, positionSelected } from '../../entities/position';
import { localeChanged, themeChanged } from '../../entities/preferences';
import { signOutFx } from '../../entities/session';
import { chartSymbolsOpened } from '../../features/edit-chart-symbols';
import { addInstrumentOpened } from '../../features/manage-watchlist';
import { openPositionRequested } from '../../features/open-position';
import { fillDialogOpened } from '../../features/record-fill';
import { LANGUAGE_NAME } from '../../shared/lib/locale';
import { SHORTCUTS, shortcutOf, type ShortcutId } from '../../shared/lib/shortcuts';
import { routes } from '../../shared/routing';
import { Dialog, DialogContent, Shortcut } from '../../shared/ui';
import { $helpOpen, $paletteOpen, helpClosed, helpOpened, paletteClosed } from './model';

interface Entry {
  id: string;
  label: string;
  icon: ReactNode;
  run: () => void;
  keywords?: string[];
  shortcut?: ShortcutId;
}

function useGroups(): { heading: string; entries: Entry[] }[] {
  const { t } = useTranslation();
  const [positions, selected] = useUnit([$positions, $selected]);
  const nextLevel = selected ? nextLevelOf(selected) : undefined;
  const actions: Entry[] = [
    {
      id: 'new-position',
      label: t('palette.newPosition'),
      icon: <Plus />,
      shortcut: 'newPosition',
      run: () => {
        openPositionRequested({});
      },
    },
    ...(selected && nextLevel
      ? [
          {
            id: 'record-fill',
            label: t('palette.recordFill', {
              symbol: selected.instrument.display_symbol,
              n: nextLevel.n,
            }),
            icon: <ShoppingCart />,
            shortcut: 'recordFill' as const,
            run: () => {
              fillDialogOpened({ position: selected, level: nextLevel.n });
            },
          },
        ]
      : []),
    {
      id: 'add-instrument',
      label: t('palette.addInstrument'),
      icon: <Eye />,
      run: () => {
        void routes.watchlist.open();
        addInstrumentOpened();
      },
    },
    {
      id: 'edit-charts',
      label: t('palette.editCharts'),
      icon: <ListOrdered />,
      run: () => {
        void routes.charts.open();
        chartSymbolsOpened();
      },
    },
  ];
  const navigation: Entry[] = [
    {
      id: 'go-positions',
      label: t('palette.goTo', { page: t('nav.positions') }),
      icon: <Layers />,
      shortcut: 'goPositions',
      run: () => void routes.positions.open(),
    },
    {
      id: 'go-alerts',
      label: t('palette.goTo', { page: t('nav.alerts') }),
      icon: <Bell />,
      shortcut: 'goAlerts',
      run: () => void routes.alerts.open(),
    },
    {
      id: 'go-stats',
      label: t('palette.goTo', { page: t('nav.stats') }),
      icon: <BarChart3 />,
      shortcut: 'goStats',
      run: () => void routes.stats.open(),
    },
    {
      id: 'go-charts',
      label: t('palette.goTo', { page: t('nav.charts') }),
      icon: <ChartCandlestick />,
      shortcut: 'goCharts',
      run: () => void routes.charts.open(),
    },
    {
      id: 'go-watchlist',
      label: t('palette.goTo', { page: t('nav.watchlist') }),
      icon: <Eye />,
      shortcut: 'goWatchlist',
      run: () => void routes.watchlist.open(),
    },
    {
      id: 'go-settings',
      label: t('palette.goTo', { page: t('nav.settings') }),
      icon: <Settings />,
      shortcut: 'goSettings',
      run: () => void routes.settings.open(),
    },
  ];
  const open: Entry[] = (positions ?? []).map((p) => ({
    id: `position-${String(p.id)}`,
    label: t('palette.openPosition', { symbol: p.instrument.display_symbol, id: p.id }),
    icon: <Layers />,
    keywords: [p.instrument.name],
    run: () => {
      positionSelected(p.id);
      void routes.position.open({ id: String(p.id) });
    },
  }));
  const preferences: Entry[] = [
    {
      id: 'theme-system',
      label: t('palette.theme', { theme: t('preferences.themes.system') }),
      icon: <Monitor />,
      run: () => {
        themeChanged('system');
      },
    },
    {
      id: 'theme-dark',
      label: t('palette.theme', { theme: t('preferences.themes.dark') }),
      icon: <Moon />,
      run: () => {
        themeChanged('dark');
      },
    },
    {
      id: 'theme-light',
      label: t('palette.theme', { theme: t('preferences.themes.light') }),
      icon: <Sun />,
      run: () => {
        themeChanged('light');
      },
    },
    {
      id: 'lang-en',
      label: t('palette.language', { language: LANGUAGE_NAME.en }),
      icon: <Languages />,
      run: () => {
        localeChanged('en');
      },
    },
    {
      id: 'lang-bg',
      label: t('palette.language', { language: LANGUAGE_NAME.bg }),
      icon: <Languages />,
      run: () => {
        localeChanged('bg');
      },
    },
    {
      id: 'shortcuts',
      label: t('palette.shortcuts'),
      icon: <Keyboard />,
      shortcut: 'help',
      run: () => {
        helpOpened();
      },
    },
    {
      id: 'sign-out',
      label: t('session.signOut'),
      icon: <LogOut />,
      run: () => void signOutFx().catch(() => undefined),
    },
  ];
  return [
    { heading: t('palette.actions'), entries: actions },
    { heading: t('palette.navigation'), entries: navigation },
    ...(open.length ? [{ heading: t('palette.positions'), entries: open }] : []),
    { heading: t('palette.preferences'), entries: preferences },
  ];
}

export function CommandPalette() {
  const [open, close] = useUnit([$paletteOpen, paletteClosed]);
  return (
    <>
      <RadixDialog.Root
        open={open}
        onOpenChange={(next) => {
          if (!next) close();
        }}
      >
        {open && <PaletteContent />}
      </RadixDialog.Root>
      <ShortcutsSheet />
    </>
  );
}

function PaletteContent() {
  const { t } = useTranslation();
  const close = useUnit(paletteClosed);
  const groups = useGroups();
  return (
    <RadixDialog.Portal>
      <RadixDialog.Overlay className="fixed inset-0 z-40 animate-fade-in bg-overlay" />
      <RadixDialog.Content
        aria-describedby={undefined}
        className="fixed top-[14vh] left-1/2 z-50 w-[min(640px,calc(100vw-32px))] -translate-x-1/2 animate-scale-in overflow-hidden rounded-lg bg-bg-2 text-text-1 shadow-popover outline-none"
      >
        <RadixDialog.Title className="sr-only">{t('palette.title')}</RadixDialog.Title>
        <Command loop label={t('palette.title')}>
          <Command.Input
            autoFocus
            placeholder={t('palette.placeholder')}
            className="h-11 w-full border-b border-border-subtle bg-transparent px-4 text-sm outline-none placeholder:text-text-4"
          />
          <Command.List className="max-h-[min(420px,60vh)] overflow-y-auto p-1.5">
            <Command.Empty className="px-3 py-6 text-center text-sm text-text-3">
              {t('palette.empty')}
            </Command.Empty>
            {groups.map((group) => (
              <Command.Group
                key={group.heading}
                heading={group.heading}
                className="[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-2xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-text-3"
              >
                {group.entries.map((entry) => (
                  <Command.Item
                    key={entry.id}
                    value={entry.label}
                    keywords={entry.keywords ?? []}
                    onSelect={() => {
                      close();
                      entry.run();
                    }}
                    className="flex h-9 cursor-pointer items-center gap-2.5 rounded-md px-2.5 text-sm text-text-2 aria-selected:bg-bg-4 aria-selected:text-text-1 [&_svg]:size-4 [&_svg]:text-text-3"
                  >
                    {entry.icon}
                    <span className="min-w-0 flex-1 truncate">{entry.label}</span>
                    {entry.shortcut && <Shortcut keys={shortcutOf(entry.shortcut)} />}
                  </Command.Item>
                ))}
              </Command.Group>
            ))}
          </Command.List>
        </Command>
      </RadixDialog.Content>
    </RadixDialog.Portal>
  );
}

function ShortcutsSheet() {
  const { t } = useTranslation();
  const [open, close] = useUnit([$helpOpen, helpClosed]);
  const groups = ['general', 'navigation', 'positions'] as const;
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      {open && (
        <DialogContent title={t('shortcuts.title')} closeLabel={t('common.close')}>
          <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
            {groups.map((group) => (
              <section key={group}>
                <h3 className="mb-1.5 text-xs font-medium text-text-3">
                  {t(`shortcuts.groups.${group}`)}
                </h3>
                <dl className="space-y-1">
                  {SHORTCUTS.filter((s) => s.group === group).map((s) => (
                    <div key={s.id} className="flex items-center justify-between gap-3 text-sm">
                      <dt className="text-text-2">{t(`shortcuts.items.${s.id}`)}</dt>
                      <dd>
                        <Shortcut keys={s.keys} />
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            ))}
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
}
